import api from './api';
import { Devotional } from '../types';

export interface UserProgress {
  id: string;
  userId: string;
  trailId: string;
  currentDay: number;
  lastViewedAt: string | null;
  nextUnlockAt: string | null;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DevotionalProgress {
  id: string;
  userId: string;
  devotionalId: string;
  isCompleted: boolean;
  watchedSeconds: number;
  watchedPercentage: number;
  completedAt: string | null;
}

export interface TodayDevotional {
  trail: {
    id: string;
    title: string;
    description: string;
    thumbnailUrl: string;
  };
  devotional: Devotional | null;
  dayNumber: number;
  totalDays: number;
  isCompleted: boolean;
  isLocked: boolean;
  unlocksAt: string | null;
}

export interface TrailWithProgress {
  trail: {
    id: string;
    title: string;
    description: string;
    thumbnailUrl: string;
  };
  progress: UserProgress;
  devotionals: (Devotional & {
    userProgress: DevotionalProgress | null;
    isUnlocked: boolean;
  })[];
}

export const progressService = {
  /**
   * Retorna os devocionais disponíveis hoje para cada trilha
   */
  getTodayDevotionals: async (): Promise<TodayDevotional[]> => {
    const response = await api.get('/progress/today');
    return response.data;
  },

  /**
   * Retorna todo o progresso do usuário
   */
  getAllProgress: async (): Promise<UserProgress[]> => {
    const response = await api.get('/progress');
    return response.data;
  },

  /**
   * Retorna progresso em uma trilha específica
   */
  getProgress: async (trailId: string): Promise<UserProgress> => {
    const response = await api.get(`/progress/trails/${trailId}`);
    return response.data;
  },

  /**
   * Retorna trilha com todos os devocionais e progresso
   */
  getTrailWithProgress: async (trailId: string): Promise<TrailWithProgress> => {
    const response = await api.get(`/progress/trails/${trailId}/full`);
    return response.data;
  },

  /**
   * Inicia uma trilha
   */
  startTrail: async (trailId: string): Promise<UserProgress> => {
    const response = await api.post(`/progress/trails/${trailId}/start`);
    return response.data;
  },

  /**
   * Marca devocional como concluído
   */
  markComplete: async (
    trailId: string,
    devotionalId: string
  ): Promise<{ trailProgress: UserProgress; devotionalProgress: DevotionalProgress }> => {
    const response = await api.post(`/progress/trails/${trailId}/complete`, { devotionalId });
    return response.data;
  },

  /**
   * Atualiza progresso de visualização
   */
  updateWatchProgress: async (
    devotionalId: string,
    watchedSeconds: number,
    watchedPercentage: number
  ): Promise<DevotionalProgress> => {
    const response = await api.post(`/progress/devotionals/${devotionalId}/watch`, {
      watchedSeconds,
      watchedPercentage,
    });
    return response.data;
  },

  /**
   * Verifica se devocional está liberado
   */
  checkUnlock: async (trailId: string, dayNumber: number): Promise<boolean> => {
    const response = await api.post(`/progress/trails/${trailId}/check-unlock`, { dayNumber });
    return response.data;
  },

  /**
   * Retorna progresso de um devocional
   */
  getDevotionalProgress: async (devotionalId: string): Promise<DevotionalProgress | null> => {
    const response = await api.get(`/progress/devotionals/${devotionalId}`);
    return response.data;
  },
};
